import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "../../hooks/redux-hooks";
import { getNotesDetails } from "../../service/notes.service";
import type { TNotes } from "../../types/index.type";
import { diffWords, textChanged } from "../../utils/wordDiff";

// The canonical note-content fields (same set the Save handler writes).
// Note-type-agnostic: only the fields a given note type uses are ever
// populated, so unchanged/empty sections drop out naturally.
const CONTENT_FIELDS: { key: keyof TNotes; label: string }[] = [
  { key: "subject", label: "Subjective" },
  { key: "objective", label: "Objective" },
  { key: "assessment", label: "Assessment" },
  { key: "plan", label: "Plan" },
  { key: "data", label: "Data" },
  { key: "intake_note", label: "Intake note" },
  { key: "eap_intake_note", label: "EAP intake note" },
  { key: "psych_intake_note", label: "Psychiatric intake" },
  { key: "psych_followup_note", label: "Psychiatric follow-up" },
  { key: "emdr_note", label: "EMDR note" },
  { key: "sol_psych_intake_note", label: "Psychiatric intake" },
  { key: "sol_psych_followup_note", label: "Psychiatric follow-up" },
  { key: "sol_therapy_intake_note", label: "Therapy intake" },
  { key: "sol_therapy_followup_note", label: "Therapy follow-up" },
  { key: "document", label: "Note" },
];

const asText = (v: unknown) => (typeof v === "string" ? v : "");

/**
 * "What changed" banner for a regenerated note version.
 *
 * Closes the loop on batched edits / chat regenerations: instead of the
 * clinician having to eyeball whether anything actually changed, this
 * fetches the parent version (`edited_from_note`) and shows a per-section
 * word-level diff. Renders nothing for original notes or when there is no
 * textual change. Reads the open note from Redux (same as NotesView).
 */
const NoteChanges: React.FC = () => {
  const { notesDetails } = useAppSelector((state) => state.notes);
  const parentId = notesDetails?.edited_from_note ?? null;

  const [parent, setParent] = useState<TNotes | null>(null);
  // Hidden by default — a regenerated note opens clean; the diff is opt-in
  // behind a quiet "View changes" link.
  const [revealed, setRevealed] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Reset when the open note changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset keyed on note id
  useEffect(() => {
    setParent(null);
    setRevealed(false);
    setDismissed(false);
  }, [notesDetails?.id]);

  useEffect(() => {
    if (!parentId) return;
    let cancelled = false;
    getNotesDetails(parentId)
      .then((res) => {
        if (!cancelled) setParent(res.data as TNotes);
      })
      .catch(() => {
        if (!cancelled) setParent(null);
      });
    return () => {
      cancelled = true;
    };
  }, [parentId]);

  const changed = useMemo(() => {
    if (!notesDetails || !parent) return [];
    return CONTENT_FIELDS.map((f) => ({
      label: f.label,
      before: asText(parent[f.key]),
      after: asText(notesDetails[f.key]),
    })).filter((s) => (s.before || s.after) && textChanged(s.before, s.after));
  }, [notesDetails, parent]);

  if (!parentId || dismissed) return null;
  if (!parent || changed.length === 0) return null;

  const sectionLabel = `${changed.length} section${changed.length === 1 ? "" : "s"} changed`;

  // Default state: hidden. Show only a quiet link so the regenerated note
  // isn't dominated by a diff banner — the clinician opts in to see changes.
  if (!revealed) {
    return (
      <button type="button" style={styles.revealLink} onClick={() => setRevealed(true)}>
        View changes from the previous version ({sectionLabel})
      </button>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <span style={styles.badge}>Edited version</span>
        <span style={styles.headerText}>{sectionLabel} from the previous version</span>
        <button type="button" style={styles.toggle} onClick={() => setRevealed(false)}>
          Hide changes
        </button>
        <button
          type="button"
          style={styles.dismiss}
          aria-label="Dismiss"
          title="Dismiss"
          onClick={() => setDismissed(true)}
        >
          ×
        </button>
      </div>

      <div style={styles.body}>
        {changed.map((section) => (
          <div key={section.label} style={styles.section}>
            <div style={styles.sectionTitle}>{section.label}</div>
            <div style={styles.diff}>
              {diffWords(section.before, section.after).map((seg, idx) => {
                if (seg.type === "same") {
                  return <span key={`s-${idx}-${seg.value.length}`}>{seg.value}</span>;
                }
                return (
                  <span
                    key={`${seg.type}-${idx}-${seg.value.length}`}
                    style={seg.type === "added" ? styles.added : styles.removed}
                  >
                    {seg.value}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  revealLink: {
    display: "inline-block",
    marginBottom: "12px",
    padding: "2px 0",
    fontSize: "12px",
    fontWeight: 500,
    color: "#6366F1",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  container: {
    marginBottom: "16px",
    borderRadius: "12px",
    border: "1px solid #C7D2FE",
    backgroundColor: "#F5F3FF",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 14px",
  },
  badge: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#4338CA",
    backgroundColor: "#E0E7FF",
    padding: "2px 8px",
    borderRadius: "999px",
    flexShrink: 0,
  },
  headerText: {
    fontSize: "13px",
    color: "#374151",
    flex: 1,
    minWidth: 0,
  },
  toggle: {
    padding: "4px 12px",
    fontSize: "12px",
    fontWeight: 500,
    borderRadius: "8px",
    border: "1px solid #C7D2FE",
    backgroundColor: "#FFFFFF",
    color: "#4338CA",
    cursor: "pointer",
    fontFamily: "inherit",
    flexShrink: 0,
  },
  dismiss: {
    width: "22px",
    height: "22px",
    lineHeight: "18px",
    padding: 0,
    fontSize: "16px",
    color: "#9CA3AF",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    flexShrink: 0,
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "0 14px 14px",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  sectionTitle: {
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    color: "#6366F1",
  },
  diff: {
    fontSize: "13px",
    lineHeight: "1.5",
    color: "#1F2937",
    whiteSpace: "pre-wrap",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E0E7FF",
    borderRadius: "8px",
    padding: "8px 10px",
    maxHeight: "260px",
    overflowY: "auto",
  },
  added: {
    backgroundColor: "#DCFCE7",
    color: "#166534",
    borderRadius: "3px",
  },
  removed: {
    backgroundColor: "#FEE2E2",
    color: "#991B1B",
    textDecoration: "line-through",
    borderRadius: "3px",
  },
};

export default NoteChanges;
